import React, { useEffect, useState, useRef } from 'react'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'

export const VolumeThreejs = ({ base64Image, demBase64Image, modelBase64Glb }) => {
    const mountRef = useRef(null)
    const [renderer, setRenderer] = useState(null)
    const [scene] = useState(new THREE.Scene())
    const [camera] = useState(new THREE.PerspectiveCamera(75, 1, 0.1, 10000))
    const [controls, setControls] = useState(null)
    const [terrain, setTerrain] = useState(null)
    const [model, setModel] = useState(null)

    const loadImageData = (base64Image: string): Promise<ImageData> => {
        return new Promise((resolve, reject) => {
            const img = new Image()
            img.onload = () => {
                const canvas = document.createElement('canvas')
                canvas.width = img.width
                canvas.height = img.height
                const ctx = canvas.getContext('2d')
                if (ctx) {
                    ctx.drawImage(img, 0, 0)
                    const imageData = ctx.getImageData(0, 0, img.width, img.height)
                    resolve(imageData)
                } else {
                    reject(new Error('Could not get canvas context'))
                }
            }
            img.onerror = reject
            img.src = `data:image/webp;base64,${base64Image}`
        })
    }

    const loadModel = async () => {
        if (modelBase64Glb) {
            const loader = new GLTFLoader();
            const arrayBuffer = Uint8Array.from(atob(modelBase64Glb), c => c.charCodeAt(0)).buffer;
            const blob = new Blob([arrayBuffer], { type: 'model/gltf-binary' });
            const url = URL.createObjectURL(blob);

            loader.load(url, (gltf) => {
                const gltfScene = gltf.scene;
                scene.add(gltfScene);
                setModel(gltfScene);
            }, undefined, (error) => {
                console.error('Error loading GLTF model:', error);
            });
        }
    }

    const createTerrain = async () => {
        const scaleY = 10 // Vertical scaling factor for height map

        const demImageData = await loadImageData(demBase64Image)
        const heatmapImageData = await loadImageData(base64Image)

        const demData = demImageData.data
        const heatmapData = heatmapImageData.data
        const width = demImageData.width
        const height = demImageData.height

        const geometry = new THREE.BufferGeometry()
        const vertices = []
        const colors = []
        const indices = []

        const size = 7500
        const halfSize = size / 2

        // Create a 2D array to map valid vertices and their indices
        const vertexMap = Array.from({ length: height }, () =>
            Array(width).fill(-1)
        )
        let vertexIndex = 0

        // Generate vertices and colors only for non-transparent areas
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const i = x + y * width
                const j = i * 4

                const alphaValue = heatmapData[j + 3]
                if (alphaValue === 0) continue

                // Generate vertices
                const heightValue = demData[j]
                const vertexX = (x / (width - 1)) * size - halfSize
                const vertexZ = (y / (height - 1)) * size - halfSize
                const vertexY = heightValue * scaleY
                vertices.push(vertexX, vertexY, vertexZ)

                // Add color to vertices
                const heatmapR = heatmapData[j]
                const heatmapG = heatmapData[j + 1]
                const heatmapB = heatmapData[j + 2]
                colors.push(heatmapR / 255, heatmapG / 255, heatmapB / 255)

                // Store the index of the valid vertex in the vertex map
                vertexMap[y][x] = vertexIndex++
            }
        }

        // Generate triangles for valid vertices
        for (let y = 0; y < height - 1; y++) {
            for (let x = 0; x < width - 1; x++) {
                const a = vertexMap[y][x]
                const b = vertexMap[y][x + 1]
                const c = vertexMap[y + 1][x]
                const d = vertexMap[y + 1][x + 1]

                // Only create triangles if all four vertices are valid
                if (a !== -1 && b !== -1 && c !== -1 && d !== -1) {
                    indices.push(a, c, d)
                    indices.push(a, d, b)
                }
            }
        }

        geometry.setAttribute(
            'position',
            new THREE.BufferAttribute(new Float32Array(vertices), 3)
        ) // 3 values per vertex
        geometry.setAttribute(
            'color',
            new THREE.BufferAttribute(new Float32Array(colors), 3)
        ) // 3 values per color (RGB)
        geometry.setIndex(indices)

        geometry.computeVertexNormals()

        const material = new THREE.MeshStandardMaterial({
            vertexColors: true,
            transparent: true,
            opacity: 1,
        })
        const terrainMesh = new THREE.Mesh(geometry, material)
        setTerrain(terrainMesh)
        return terrainMesh
    }

    useEffect(() => {
        const initScene = () => {
            camera.aspect =
                mountRef.current.clientWidth / mountRef.current.clientHeight
            camera.updateProjectionMatrix()
            camera.position.set(3000, 3000, 3000)

            const ambientLight = new THREE.AmbientLight(0xffffff, 0.7)
            scene.add(ambientLight)

            const directionalLight = new THREE.DirectionalLight(0xffffff, 1)
            directionalLight.position.set(500, 2000, 2000).normalize()
            scene.add(directionalLight)
        }
        initScene()
    }, [])

    const cleanUpScene = () => {
        if (terrain) {
            scene.remove(terrain)
            setTerrain(null)
        }
        if (model) {
            scene.remove(model)
            setModel(null)
        }
    }

    useEffect(() => {
        const visualize = async () => {
            let rendererInstance = renderer
            if (!rendererInstance) {
                rendererInstance = new THREE.WebGLRenderer({ alpha: true })
                rendererInstance.setSize(
                    mountRef.current.clientWidth,
                    mountRef.current.clientHeight
                )
                rendererInstance.setClearColor(0xdce3e4)
                mountRef.current.appendChild(rendererInstance.domElement)
                setRenderer(rendererInstance)
            }

            await loadModel();

            const terrain = await createTerrain()
            scene.add(terrain)

            let controlsInstance = controls
            if (!controlsInstance) {
                controlsInstance = new OrbitControls(
                    camera,
                    rendererInstance.domElement
                )
                controlsInstance.enableDamping = true
                controlsInstance.dampingFactor = 0.05
                controlsInstance.maxPolarAngle = Math.PI / 2
                controlsInstance.minDistance = 1000
                controlsInstance.maxDistance = 10000
                setControls(controlsInstance)
            }

            const animate = () => {
                requestAnimationFrame(animate)
                controlsInstance.update()
                rendererInstance.render(scene, camera)
            }

            animate()
        }
        cleanUpScene()
        visualize().then(() => console.log('Scene loaded'))
    }, [demBase64Image, base64Image])

    const useResizeObserver = (ref, onResize) => {
        useEffect(() => {
            const resizeObserver = new ResizeObserver(entries => {
                if (entries[0].contentRect) {
                    onResize(entries[0].contentRect)
                }
            })
            if (ref.current) {
                resizeObserver.observe(ref.current)
            }
            return () => resizeObserver.disconnect()
        }, [ref, onResize])
    }

    useResizeObserver(mountRef, ({ width, height }) => {
        if (renderer && camera) {
            renderer.setSize(width, height)
            camera.aspect = width / height
            camera.updateProjectionMatrix()
        }
    })

    return <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
}
